import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * tailwind-merge taught about the GrowHero theme (packages/brand/src/theme.css), so that
 * `cn('text-2xs', 'text-ink')` keeps both (size + colour) and `cn('bg-surface', 'bg-primary')`
 * lets the later class win. Keep these lists in sync with theme.css.
 */
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        '2xs',
        'xs',
        'sm',
        'base',
        'md',
        'lg',
        'lede',
        'headline',
        'xl',
        '2xl',
        '3xl',
        '4xl',
        '5xl',
        '6xl',
      ],
      shadow: ['line', 'xs', 'sm', 'md', 'lift', 'lg', 'inset'],
      radius: ['xs', 'sm', 'md', 'lg', 'xl'],
      tracking: ['display', 'headline', 'tightest', 'tight', 'eyebrow'],
      color: [
        'white',
        'black',
        'canvas',
        'canvas-deep',
        'paper',
        'paper-deep',
        'field',
        'field-deep',
        'field-tonal',
        'field-tonal-strong',
        'field-raised',
        'field-line',
        'on-field',
        'on-field-muted',
        'on-field-subtle',
        'field-accent',
        'field-accent-text',
        'field-leaf',
        'field-focus',
        'focus-on-field',
        'surface',
        'surface-raised',
        'sunken',
        'overlay',
        'ink',
        'ink-soft',
        'ink-muted',
        'ink-subtle',
        'ink-inverse',
        'hairline',
        'hairline-strong',
        'control-border',
        'rule',
        'primary',
        'primary-strong',
        'primary-hover',
        'primary-soft',
        'primary-ink',
        'on-primary',
        'accent',
        'accent-strong',
        'accent-soft',
        'accent-ink',
        'on-accent',
        'area-ai',
        'area-ai-soft',
        'area-ai-ink',
        'area-reviews',
        'area-reviews-soft',
        'area-reviews-ink',
        'area-messages',
        'area-messages-soft',
        'area-messages-ink',
        'negative',
        'negative-strong',
        'negative-soft',
        'negative-ink',
        'danger-fill',
        'danger-fill-hover',
        'critical',
        'critical-soft',
        'critical-ink',
        'info',
        'info-soft',
        'info-ink',
        'success',
        'success-soft',
        'success-ink',
        'warning',
        'warning-soft',
        'warning-ink',
        'link',
        'focus',
        'chart-1',
        'chart-2',
        'chart-3',
        'chart-4',
        'chart-5',
        'chart-6',
        'chart-current',
        'chart-muted',
        'chart-grid',
        'chart-axis',
      ],
    },
  },
});

/** Class name joiner with conflict resolution: later classes override earlier ones. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
